import { cn } from '@/lib/utils';
import type { AgentId } from '@/hooks/useJobProgress';

interface AgentAvatarProps {
  agent: AgentId;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * A small monogram avatar for an agent. Used in the sidebar, in the
 * panel header, and inline next to telemetry events. The single ochre
 * accent is reserved for the currently-active agent (handled by callers).
 */
const LABELS: Record<AgentId, string> = {
  ora: 'OR',
  scry: 'SC',
  loom: 'LO',
  lex: 'LE',
  lumen: 'LU',
  mosaic: 'MO',
  forge: 'FO',
};

const FULL_NAMES: Record<AgentId, string> = {
  ora: 'ORA',
  scry: 'SCRY',
  loom: 'LOOM',
  lex: 'LEX',
  lumen: 'LUMEN',
  mosaic: 'MOSAIC',
  forge: 'FORGE',
};

export function AgentAvatar({ agent, size = 'md', className }: AgentAvatarProps) {
  const sizeClass =
    size === 'sm'
      ? 'h-6 w-6 text-[9px]'
      : size === 'lg'
        ? 'h-10 w-10 text-xs'
        : 'h-8 w-8 text-[10px]';

  return (
    <span
      title={FULL_NAMES[agent]}
      className={cn(
        'inline-flex items-center justify-center rounded-sm border border-ink/20 bg-paper-2 font-mono font-medium tracking-widest text-ink/80',
        sizeClass,
        className,
      )}
      aria-label={FULL_NAMES[agent]}
    >
      {LABELS[agent]}
    </span>
  );
}

export function agentFullName(agent: AgentId): string {
  return FULL_NAMES[agent];
}
